import { z } from 'zod';
import {
  SPACES_SCHEMA_VERSION,
  SPACE_ID_PATTERN,
  MAX_SPACES,
  MAX_SPACE_NAME_CHARS,
} from './constants.js';
import { BoardSchema } from './board.js';

/**
 * Spaces — multiple named boards, one active (iOS home-screen "pages").
 *
 * A Spaces envelope wraps N full boards, each labelled, plus a pointer to the one
 * currently shown. It is the top-level persisted unit for users who have more than
 * one board; a single-board user is just the N=1 case (which is what the legacy
 * migration produces — see spaces-migrate.ts).
 *
 * SECURITY / INTEGRITY (same discipline as BoardSchema, ADR-002 §7):
 *   - `.strict()` throughout → no smuggled top-level or per-space fields.
 *   - Each `board` is a full BoardSchema, so the entire tile/clone firewall
 *     (SSRF allowlist, tile-count cap, referential integrity) applies UNCHANGED to
 *     every board inside every space. Spaces adds NO new trust surface per board.
 *   - Envelope-level invariants via superRefine: 1..MAX_SPACES spaces, unique ids,
 *     unique names (case-insensitive), and activeSpaceId must name a real space.
 *
 * NAME RULE (ruling): names are UNIQUE CASE-INSENSITIVELY. Two spaces named "Work"
 * and "work" would render as indistinguishable page dots — a confusing, un-calm UX.
 * Rejecting the collision at the schema boundary keeps the picker legible. Names are
 * also STRICTLY TRIMMED: a name with surrounding whitespace is REJECTED, not silently
 * trimmed. The app trims user input before it commits; the schema is the guard that
 * proves it did. (Same posture as BoardSchema — parse validates, it does not repair.)
 *
 * SCOPE NOTE: tile ids are unique PER BOARD, never across the whole envelope. Two
 * different spaces may each contain `tile_clock1`; that is fine and intended — each
 * board is its own id namespace (this is why duplicateSpace keeps tile ids as-is and
 * only regenerates the board id).
 */

/** A space name: trimmed plain text, 1..MAX_SPACE_NAME_CHARS. Rendered as text, never HTML. */
const spaceNameSchema = z
  .string()
  .min(1)
  .max(MAX_SPACE_NAME_CHARS)
  // Reject (do not repair) surrounding whitespace: a trimmed name is the contract.
  .refine((s) => s === s.trim(), { message: 'name must be trimmed (no leading/trailing whitespace)' });

/** A single space: a labelled, full board with a local (no-PII) id. */
const spaceSchema = z
  .object({
    id: z.string().regex(SPACE_ID_PATTERN),
    name: spaceNameSchema,
    board: BoardSchema,
  })
  .strict();

/** Inferred output type — one validated, immutable space. */
export type Space = Readonly<z.infer<typeof spaceSchema>>;

export const SpacesSchema = z
  .object({
    schemaVersion: z.literal(SPACES_SCHEMA_VERSION),
    spaces: z.array(spaceSchema).min(1).max(MAX_SPACES),
    activeSpaceId: z.string().regex(SPACE_ID_PATTERN),
  })
  .strict()
  .superRefine((envelope, ctx) => {
    // --- unique space ids ---
    const ids = new Set<string>();
    envelope.spaces.forEach((space, i) => {
      if (ids.has(space.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['spaces', i, 'id'],
          message: `duplicate space id "${space.id}"`,
        });
      }
      ids.add(space.id);
    });

    // --- unique names, CASE-INSENSITIVE (the ruling) ---
    const names = new Set<string>();
    envelope.spaces.forEach((space, i) => {
      const key = space.name.toLowerCase();
      if (names.has(key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['spaces', i, 'name'],
          message: `duplicate space name "${space.name}" (names are unique case-insensitively)`,
        });
      }
      names.add(key);
    });

    // --- activeSpaceId must name a real space (referential integrity) ---
    if (!ids.has(envelope.activeSpaceId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['activeSpaceId'],
        message: `activeSpaceId "${envelope.activeSpaceId}" references no space`,
      });
    }
  });

/** Inferred output type — a validated, immutable Spaces envelope. */
export type Spaces = Readonly<z.infer<typeof SpacesSchema>>;

/** Throwing parse — for trusted call sites that want an exception on bad input. */
export function parseSpaces(input: unknown): Spaces {
  return SpacesSchema.parse(input);
}

/** Non-throwing parse — for graceful-degradation boundaries (ADR-002 §4). */
export function safeParseSpaces(input: unknown): z.SafeParseReturnType<unknown, Spaces> {
  return SpacesSchema.safeParse(input);
}
